import type { AppRouter } from '@travel-planner/api/src/routers';
import { transformer } from '@travel-planner/shared/trpc/transformer';
import {
  createTRPCClient,
  httpBatchLink,
  httpBatchStreamLink,
  httpLink,
  splitLink,
} from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';

export const trpc = createTRPCReact<AppRouter>();

type ClerkGlobal = {
  loaded?: boolean;
  session?: { getToken: () => Promise<string | null> };
  addListener?: (cb: (event: { session?: unknown }) => void) => void;
};

const waitForClerk = (): Promise<ClerkGlobal | null> => {
  const w = window as { Clerk?: ClerkGlobal };
  if (w.Clerk?.loaded) return Promise.resolve(w.Clerk);

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 3000);

    const check = () => {
      if (w.Clerk?.loaded) {
        clearTimeout(timeout);
        resolve(w.Clerk);
      }
    };

    if (w.Clerk?.addListener) {
      w.Clerk.addListener(check);
    } else {
      const interval = setInterval(() => {
        if (w.Clerk?.loaded) {
          clearInterval(interval);
          clearTimeout(timeout);
          resolve(w.Clerk);
        }
      }, 50);
    }
  });
};

const devBypassAuth = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

const getClerkToken = async (): Promise<string | null> => {
  if (devBypassAuth) return 'dev-bypass';
  const clerk = await waitForClerk();
  if (!clerk?.session) return null;
  return clerk.session.getToken().catch(() => null);
};

// Default getter reads from Clerk directly.
// Once React mounts, setTRPCTokenGetter overrides with useAuth().getToken.
const authToken: { get: () => Promise<string | null> } = {
  get: getClerkToken,
};

export const setTRPCTokenGetter = (getter: () => Promise<string | null>) => {
  authToken.get = getter;
};

export const getAuthToken = () => authToken.get();

const url = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const headers = async () => {
  const token = await authToken.get();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Link selection per call:
// - `context: { stream: true }` → `httpBatchStreamLink`, which streams a
//   procedure's yielded async-iterable chunks to the client as they arrive
//   (used by the agent loop's live step progress — `agentRunStream`).
// - `context: { skipBatch: true }` → unbatched `httpLink`, so one slow call
//   doesn't hold up faster ones in the same batch.
// - otherwise → batched `httpLink`.
// All three reuse the same `headers` getter, so Clerk auth works on every
// transport. See `.claude/rules/trpc-transformer.md`.
const links = [
  splitLink({
    condition: (op) => op.context.stream === true,
    true: httpBatchStreamLink({ url, transformer, headers }),
    false: splitLink({
      condition: (op) => op.context.skipBatch === true,
      true: httpLink({ url, transformer, headers }),
      false: httpBatchLink({ url, transformer, headers }),
    }),
  }),
];

// React hooks client — use in components
export const trpcClient = trpc.createClient({ links });

// Vanilla client — use in route loaders and non-React contexts
export const trpcVanilla = createTRPCClient<AppRouter>({ links });
